import type { QuizRatingDto, QuizRatingSummaryDto } from '@klurigo/common'
import { type FC } from 'react'

import {
  HorizontalRail,
  Stack,
  StarRating,
  Typography,
} from '../../../../../../components'
import { formatTimeAgo } from '../../../../../../utils/date.utils'

import styles from './RatingsSection.module.scss'

/** Number of skeleton placeholder cards shown while ratings are loading. */
export const RATINGS_SKELETON_COUNT = 3

export type RatingsSectionProps = {
  /** Summary stats (average stars + comment count) for the quiz. */
  readonly summary: QuizRatingSummaryDto
  /** Individual ratings to display in the horizontal rail. */
  readonly ratings: QuizRatingDto[]
  /** When true, renders skeleton placeholder cards instead of real data. */
  readonly isLoading?: boolean
}

/**
 * Displays a "Ratings & Reviews" section for a quiz details page.
 *
 * Renders a summary panel showing the average star score and comment count,
 * followed by a horizontally scrollable rail of individual rating cards.
 * When `isLoading` is true, skeleton placeholder cards are shown in the rail.
 * When there are no written reviews, an empty state message is displayed.
 * Left/right arrow buttons appear on hover (desktop) to page through the rail.
 */
const RatingsSection: FC<RatingsSectionProps> = ({
  summary,
  ratings,
  isLoading = false,
}) => {
  const renderRail = () => {
    if (isLoading) {
      return (
        <HorizontalRail className={styles.railMargin}>
          {Array.from({ length: RATINGS_SKELETON_COUNT }, (_, i) => (
            <div
              key={i}
              className={styles.skeleton}
              data-testid="ratings-skeleton-card">
              <div className={styles.skeletonLine} />
              <div className={styles.skeletonLine} />
              <div className={styles.skeletonLine} />
            </div>
          ))}
        </HorizontalRail>
      )
    }

    if (ratings.length === 0) {
      return (
        <Typography
          variant="body2"
          align="center"
          color="inverse"
          data-testid="ratings-empty-state">
          No written reviews yet
        </Typography>
      )
    }

    return (
      <HorizontalRail className={styles.railMargin}>
        {ratings.map((rating) => (
          <div
            key={rating.id}
            className={styles.card}
            data-testid="rating-card">
            <div className={styles.cardHeader}>
              <StarRating value={rating.stars} size="small" />
              <Typography variant="control2" align="right" color="subtle">
                {formatTimeAgo(rating.updatedAt)}
              </Typography>
            </div>
            <Typography variant="control" bold>
              {rating.author.nickname}
            </Typography>
            {rating.comment && (
              <Typography variant="control2">{rating.comment}</Typography>
            )}
          </div>
        ))}
      </HorizontalRail>
    )
  }

  return (
    <Stack
      as="section"
      width="full"
      spacing="compact"
      data-testid="ratings-section">
      <Typography variant="title3" align="center" color="inverse">
        Ratings &amp; Reviews
      </Typography>

      <div className={styles.summary} data-testid="ratings-summary">
        <div className={styles.score}>
          <Typography
            variant="title"
            align="center"
            color="inverse"
            aria-label={`Average rating: ${summary.stars.toFixed(1)}`}>
            {summary.stars.toFixed(1)}
          </Typography>
          <Typography variant="control" align="center" color="inverse">
            out of 5
          </Typography>
        </div>
        <div className={styles.summaryRight}>
          <StarRating value={summary.stars} size="small" />
          <Typography
            variant="control"
            align="center"
            color="inverse"
            data-testid="ratings-count">
            {summary.total} {summary.total > 1 ? 'Ratings' : 'Rating'}
          </Typography>
        </div>
      </div>

      {renderRail()}
    </Stack>
  )
}

export default RatingsSection
